/**
 * Centralized Constants & Types
 */

// Auto-detect API URL based on window.location for mobile device testing
const getApiUrl = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // If accessing from non-localhost (e.g., mobile device), use the host's IP
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    return `${protocol}//${hostname}:3000`;
  }
  
  return 'http://localhost:3000';
};

const getWsUrl = () => {
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL;
  }
  
  // If accessing from non-localhost (e.g., mobile device), use the host's IP
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    const hostname = window.location.hostname;
    return `ws://${hostname}:3000`;
  }
  
  return 'ws://localhost:3000';
};

export const API_URL = getApiUrl();
export const WS_URL = getWsUrl();

// Performance budgets
export const PERFORMANCE = {
  TTI_TARGET_MS: 2500,
  INTERACTION_TARGET_MS: 250,
  BUNDLE_SIZE_KB: 500,
} as const;

// API endpoints
export const ENDPOINTS = {
  AUTH: {
    SEND_OTP: '/api/auth/send-otp',
    VERIFY_OTP: '/api/auth/verify-otp',
    REFRESH: '/api/auth/refresh',
    LOGOUT: '/api/auth/logout',
  },
  VENDOR: {
    PROFILE: '/api/vendor/profile',
    STATS: '/api/vendor/stats',
    UPDATE_STATUS: '/api/vendor/status',
  },
  ORDERS: {
    LIST: '/api/vendor/orders',
    ACCEPT: '/api/vendor/orders/:id/accept',
    REJECT: '/api/vendor/orders/:id/reject',
    UPDATE_STATUS: '/api/vendor/orders/:id/status',
  },
  PRODUCTS: {
    LIST: '/api/vendor/products',
    CREATE: '/api/vendor/products',
    UPDATE: '/api/vendor/products/:id',
    DELETE: '/api/vendor/products/:id',
    TOGGLE: '/api/vendor/products/:id/toggle',
    STOCK: '/api/vendor/products/:id/stock',
  },
  CHAT: {
    CREATE_THREAD: '/api/chat/:orderId/thread',
    GET_MESSAGES: '/api/chat/:threadId',
    SEND_MESSAGE: '/api/chat/:threadId/message',
    TYPING: '/api/chat/:threadId/typing',
  },
  EARNINGS: {
    SUMMARY: '/api/vendor/earnings/summary',
    DAILY: '/api/vendor/earnings/daily',
    WEEKLY: '/api/vendor/earnings/weekly',
    MONTHLY: '/api/vendor/earnings/monthly',
  },
  ALERTS: {
    LIST: '/api/vendor/alerts',
    MARK_READ: '/api/vendor/alerts/:id/read',
    STATS: '/api/vendor/alerts/stats',
  },
  PROMOS: {
    LIST: '/api/vendor/promos',
    DETAILS: '/api/vendor/promos/:id',
    STATS: '/api/vendor/promos/stats',
  },
} as const;

// WebSocket channels
export const WS_CHANNELS = {
  ORDERS_VENDOR: 'order-updates',
  CHAT: 'chat-messages',
  ALERTS: 'system-alerts',
} as const;

// Order statuses
export type OrderStatus =
  | 'pending'
  | 'accepted'
  | 'preparing'
  | 'ready_for_pickup'
  | 'picked_up'
  | 'in_transit'
  | 'delivered'
  | 'cancelled';

// Vendor status
// Sprint 27: Aligned with database enum
export type VendorStatus = 'pending' | 'active' | 'suspended' | 'inactive';

// User role
export type UserRole = 'vendor' | 'buyer' | 'rider' | 'admin';

// Order interface
export interface Order {
  id: string;
  orderId: string;
  customerId: string;
  customerName: string;
  items: OrderItem[];
  totalAmount: number;
  status: OrderStatus;
  paymentMethod: string;
  deliveryAddress: string;
  createdAt: string;
  updatedAt: string;
  estimatedReadyTime?: string;
  riderId?: string;
  riderName?: string;
  // Sprint 27: Additional fields
  notes?: string;
  ready_at?: string;
}

export interface OrderItem {
  id: string;
  productId: string;
  name: string;
  quantity: number;
  price: number;
  subtotal: number;
}

// Vendor profile interface
export interface VendorProfile {
  id: string;
  name: string;
  phone: string;
  email?: string;
  status: VendorStatus;
  zoneId: string;
  zoneName: string;
  capacity: number;
  averagePrepTime: number;
  rating?: number;
  totalOrders?: number;
  // Sprint 27: Market context
  market_id?: string;
  market_name?: string;
  market_status?: boolean; // is market active?
  market_type?: 'primary' | 'satellite';
}

// Product/Menu item interface
export interface Product {
  id: string;
  vendor_id: string;
  name: string;
  description: string | null;
  category: string | null;
  unit: string;
  price_today: string;
  image_url: string | null; // Legacy field
  primary_image_id: string | null; // Sprint 4I
  gallery_image_ids: string[] | null; // Sprint 4I
  primaryImage?: ProductImage | null; // Sprint 4I: Resolved URLs
  galleryImages?: ProductImage[]; // Sprint 4I: Resolved URLs
  is_active: boolean;
  stock_status: string | null;
  created_at: string;
  updated_at: string;
}

// Sprint 4I: Image data structure
export interface ProductImage {
  id: string;
  smUrl: string;
  mdUrl: string;
  lgUrl: string;
  width?: number;
  height?: number;
}

export type StockStatus = 'in_stock' | 'low_stock' | 'out_of_stock';

// Chat message interface
export interface ChatMessage {
  id: string;
  threadId: string;
  senderId: string;
  senderName: string;
  senderRole: UserRole;
  message: string;
  timestamp: string;
  read: boolean;
}

// Earnings summary interface
export interface EarningsSummary {
  today: number;
  week: number;
  month: number;
  totalOrders: number;
  cancelRate: number;
  avgPrepTime: number;
}

// Alert severity
export type AlertSeverity = 'info' | 'warning' | 'critical';

// Alert interface
export interface Alert {
  id: string;
  alert_type: string;
  severity: AlertSeverity;
  message: string;
  metadata?: any;
  resolved: boolean;
  resolved_at?: string;
  created_at: string;
}

// Promo kind
export type PromoKind = 'percentage' | 'fixed_amount' | 'free_delivery';

// Promo interface
export interface Promo {
  id: string;
  code: string;
  kind: PromoKind;
  value: string;
  caps_json?: any;
  starts_at: string;
  ends_at: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

